import React, { CSSProperties, SyntheticEvent, useEffect, useMemo, useState } from "react";

import { Icon } from "semantic-ui-react";

import { Table } from "react-lib/frameworks/Table";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SearchBoxWithKey from "react-lib/appcon/common/SearchBoxWithKey";

import ModConfirmCNMI from "react-lib/apps/common/cnmi/ModConfirm";

import CardEmployeeTimeAttendanceUX from "./CardEmployeeTimeAttendanceUX";
import ModAddTimeManually from "./ModAddTimeManually";
import ModEditTime from "./ModEditTime";

import {
  ACTIONS,
  BTN_ACTS,
  MasterOptions,
  OnEvent,
  PickedState,
  RunSequence,
  SetProp,
  State,
  ClockRecordType,
  SEQUENCE_NAME,
  LIST_LIMIT,
} from "./sequence/EmployeeTA";

import { LOG_TYPES_OPTIONS } from "./HRMInterface";

// Serializer
import ClockRecordSerializer from "issara-sdk/types/ClockRecordSerializer_apps_HRM";
import ClockRecordHistorySerializer from "issara-sdk/types/ClockRecordHistorySerializer_apps_HRM";

// Utils
import { formatISOtoBEDateTime } from "react-lib/utils/dateUtils";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";

// Types
type CardEmployeeTimeAttendanceProps = Pick<
  PickedState,
  "buttonLoadCheck" | "errorMessage" | "successMessage" | "searchedItemListWithKey"
> & {
  onEvent: OnEvent;
  setProp: SetProp;
  runSequence: RunSequence;
  EmployeeTASequence: State["EmployeeTASequence"];
  languageUX?: any;
  readOnly?: boolean;
  style?: CSSProperties;
  divisionOptions: Record<string, any>[]
  masterOptions: MasterOptions;
};

type Styles = Record<"center", CSSProperties>;

const styles: Styles = {
  center: { display: "flex", justifyContent: "center" },
};

const CardEmployeeTimeAttendance = (props: CardEmployeeTimeAttendanceProps) => {
  const [modEditTime, setModEditTime] = useState<{
    data: ClockRecordType;
    open: boolean;
  } | null>(null);
  const [openModAddManually, setOpenModAddManually] = useState<boolean>(false);
  const [openModHistory, setOpenModHistory] = useState<boolean>(false);

  // Clear Data + Mod After Success
  useEffect(() => {
    if (props.successMessage?.[SEQUENCE_NAME]) {
      handleCloseModEditTime();
      handleCloseModAddManually();
    }
  }, [props.successMessage]);

  useEffect(() => {
    props.runSequence({ sequence: "EmployeeTA", restart: true, isSelf: props.readOnly });
  }, []);

  const logTypeOptions = useMemo(
    () => [{ key: "ALL", text: "ALL", value: "ALL" }, ...LOG_TYPES_OPTIONS],
    []
  );

  const addManualData = useMemo(
    () => props.EmployeeTASequence?.addManual || {},
    [props.EmployeeTASequence?.addManual]
  );

  const editData = useMemo(
    () => props.EmployeeTASequence?.selectDetail || {},
    [props.EmployeeTASequence?.selectDetail]
  );

  const selectedEditTime = useMemo(() => modEditTime?.data || {}, [modEditTime?.data]);

  const formattedItems = useMemo(
    () =>
      (props.EmployeeTASequence?.pagination?.items || []).map((item) => {
        const { date, time } = formatISOtoBEDateTime(item.clock_datetime);

        const enhancedItem = {
          ...item,
          date,
          time,
        };

        return {
          ...enhancedItem,
          action: (
            <div style={styles.center}>
              {!props.readOnly && (
                <div
                  id={`CardEmployeeTimeAttendance-Div-EditTime-${item.id}`}
                  onClick={() => handleEditTime(enhancedItem)}
                  style={{
                    alignItems: "center",
                    color: "rgba(234, 179, 8, 1)",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    marginRight: "1.5rem",
                  }}
                >
                  <Icon name="edit outline" style={{ fontSize: "1.15rem" }} />
                  <label style={{ fontSize: "0.6rem" }}>EDIT TIME</label>
                </div>
              )}
              <div
                id={`CardEmployeeTimeAttendance-Div-History-${item.id}`}
                onClick={() => handleSelectHistory(item.id)}
                style={{
                  alignItems: "center",
                  color: "rgba(33, 133, 208, 1)",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Icon name="history" style={{ fontSize: "1.15rem" }} />
                <label style={{ fontSize: "0.6rem" }}>HISTORY</label>
              </div>
            </div>
          ),
          employee: <div style={styles.center}>{item.employee_code}</div>,
          date: <div style={styles.center}>{date}</div>,
          type: (
            <div style={styles.center}>
              {item.type === "I" ? <label>Clock-in</label> : <label>Clock-out</label>}
            </div>
          ),
          status_label: (
            <div style={styles.center}>
              <label>{item.status_label}</label>
            </div>
          ),
          time: (
            <div
              style={{ display: "grid", gridTemplateColumns: "1fr auto", padding: "0.25 1.25rem" }}
            >
              <div>{time}</div>
              <div>
                {item.status === "I" ? (
                  <Icon color="red" name="remove circle" />
                ) : (
                  <Icon color="green" name="check circle" />
                )}
              </div>
            </div>
          ),
        };
      }),
    [props.EmployeeTASequence?.pagination?.items, props.readOnly]
  );

  const formattedHistoryItems = useMemo(
    () =>
      (props.EmployeeTASequence?.historyList || []).map((item) => ({
        ...item,
        clock_datetime: (
          <div style={styles.center}>{formatISOtoBEDateTime(item.clock_datetime).time}</div>
        ),
        status_label: (
          <div style={styles.center}>
            {item.status_label}
          </div>
        ),
      })),
    [props.EmployeeTASequence?.historyList]
  );

  const handleChangeFilter = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name = data.name;

    props.setProp("EmployeeTASequence.filter", {
      ...props.EmployeeTASequence?.filter,
      [name]: data.value,
    });
  };

  const handleChangeManual = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name = data.name;

    props.setProp("EmployeeTASequence.addManual", {
      ...props.EmployeeTASequence?.addManual,
      [name]: data.value,
    });
  };

  const handleChangeEdit = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const { name, checked, value } = data;

    let newValue;

    if (name === "status") {
      newValue = checked ? "" : "I";
    } else {
      newValue = typeof checked === "boolean" ? checked : value;
    }

    props.setProp("EmployeeTASequence.selectDetail", {
      ...props.EmployeeTASequence?.selectDetail,
      [name]: newValue,
    });
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "EmployeeTA",
      action: ACTIONS.SEARCH,
      activePage: props.EmployeeTASequence?.pagination?.activePage,
      isSelf: props.readOnly,
    });
  };

  const handleSelectHistory = (id?: number) => {
    if (!id) return;

    setOpenModHistory(true);

    props.runSequence({
      sequence: "EmployeeTA",
      action: ACTIONS.HISTORY,
      clockId: id,
    });
  };

  const handleEditTime = (item: ClockRecordType) => {
    setModEditTime({ data: item, open: true });

    props.setProp("EmployeeTASequence.selectDetail", { status: item.status });
  };

  const handleCloseModEditTime = () => {
    setModEditTime(null);

    props.setProp("EmployeeTASequence.selectDetail", {});
  };

  const handleOpenModAddManually = () => {
    setOpenModAddManually(true);
  };

  const handleCloseModAddManually = () => {
    setOpenModAddManually(false);

    props.setProp("EmployeeTASequence.addManual", {});
  };

  const handleCloseModHistory = () => {
    setOpenModHistory(false);
  };

  const handleAddManually = () => {
    props.runSequence({
      sequence: "EmployeeTA",
      action: ACTIONS.ADD,
    });
  };

  const handleSaveEditTime = (id?: number, item?: ClockRecordType) => {
    if (!id) return;

    props.runSequence({
      sequence: "EmployeeTA",
      action: ACTIONS.EDIT,
      clockId: id,
      item,
    });
  };

  const handlePageChange = (activePage: number) => {
    props.runSequence({
      sequence: "EmployeeTA",
      action: ACTIONS.SEARCH,
      activePage,
    });
  };

  return (
    <div style={{ ...props.style }}>
      <SnackMessage
        // func
        onEvent={props.onEvent}
        onClose={() => {
          props.setProp(`errorMessage.${SEQUENCE_NAME}`, null);
          props.setProp(`successMessage.${SEQUENCE_NAME}`, null);
        }}
        error={props.errorMessage?.[SEQUENCE_NAME]}
        success={props.successMessage?.[SEQUENCE_NAME]}
        languageUX={props.languageUX}
      />

      <CardEmployeeTimeAttendanceUX
        filter={props.EmployeeTASequence?.filter}
        readOnly={props.readOnly}
        tableData={formattedItems}
        divisionOptions={props.divisionOptions}
        logTypeOptions={logTypeOptions}
        onChangeFilter={handleChangeFilter}
        onClickAddManually={handleOpenModAddManually}
        buttonSearch={
          <ButtonLoadCheck
            id="CardEmployeeTimeAttendance-ButtonLoadCheck-Search"
            setProp={props.setProp}
            color="blue"
            paramKey={BTN_ACTS.SEARCH}
            size="small"
            title="SEARCH"
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
            onClick={handleSearch}
          />
        }
        employeeSearchBox={
          <SearchBoxWithKey
            onEvent={props.onEvent}
            id="FilterEmplyee"
            placeholder="Search Employee"
            selectedItem={props.EmployeeTASequence?.filter?.employee || null}
            type="UserEmployee"
            inline
            searchedItemListWithKey={props.searchedItemListWithKey}
            setSelected={(value) => {
              props.setProp("EmployeeTASequence.filter.employee", value);
            }}
          />
        }
        pagination={
          <PaginationShorthand
            activePage={props.EmployeeTASequence?.pagination?.activePage || 1}
            limit={LIST_LIMIT}
            total={props.EmployeeTASequence?.pagination?.total}
            onPageChange={handlePageChange}
          />
        }
      />

      <ModEditTime
        open={!!modEditTime?.open}
        onSave={() => handleSaveEditTime(selectedEditTime?.id, selectedEditTime)}
        onClose={handleCloseModEditTime}
        onChange={handleChangeEdit}
        selectedData={selectedEditTime}
        editData={editData}
      />

      <ModAddTimeManually
        onEvent={props.onEvent}
        setProp={props.setProp}
        EmployeeTASequence={props.EmployeeTASequence}
        open={openModAddManually}
        searchedItemListWithKey={props.searchedItemListWithKey}
        onAdd={handleAddManually}
        onClose={handleCloseModAddManually}
        onChange={handleChangeManual}
        addData={addManualData}
      />

      <ModConfirmCNMI
        testId="-History"
        backgroundColor="rgba(93, 188, 210, 1)"
        customGridGroup={{ display: "none" }}
        modalStyle={{ minWidth: "40rem" }}
        openModal={openModHistory}
        titleName={"History"}
        onCloseWithDimmerClick={handleCloseModHistory}
        content={
          <Table
            headers="Time,Status,User,Remark"
            keys="clock_datetime,status_label,edit_user_full_name,reason"
            minRows={3}
            showPagination={false}
            style={{ margin: "-1rem 0 -2rem", overflowY: "auto" }}
            data={formattedHistoryItems}
          />
        }
      />
    </div>
  );
};

CardEmployeeTimeAttendance.displayName = "CardEmployeeTimeAttendance";

export default React.memo(CardEmployeeTimeAttendance);
